import { useState } from 'react';
import { useStores } from '../../../stores';
import properties from '../../properties';
import axios from 'axios';
import { MonthMap } from '../../helper-utils/EditDuration';
import { formatDate, getDateFromMonthAndYear } from '../../helper-utils/helper';
import moment from 'moment';

export const getStartDateAndEndDate = () => {
  const employerGroupStore = useStores().employerGroupStore;

  const plan_start_month =
    employerGroupStore.employerGroupResponseData.plan_start_month;
  const plan_end_month =
    employerGroupStore.employerGroupResponseData.plan_end_month;
  const recent_plan_year =
    employerGroupStore.employerGroupResponseData.recent_plan_year;

  const startMonthIndex = MonthMap.get(plan_start_month)?.monthNumber || 1;
  const endMonthIndex = MonthMap.get(plan_end_month)?.monthNumber || 12;

  const startYear =
    startMonthIndex <= endMonthIndex ? recent_plan_year : recent_plan_year - 1;
  const endYear = recent_plan_year;

  const startDate = getDateFromMonthAndYear(
    employerGroupStore.employerGroupResponseData.plan_start_month,
    startYear,
    false,
  );
  const endDate = getDateFromMonthAndYear(
    employerGroupStore.employerGroupResponseData.plan_end_month,
    endYear,
    true,
  );

  return { startDate, endDate };
};
export type PlanDates = {
  incurred_start_date: string;
  incurred_end_date: string;
  paid_start_date: string;
  paid_end_date: string;
  current: boolean;
};
export const formatPlanPeriodDates = (planDates: PlanDates) => {
  return {
    incurred_start_date: formatDate(
      planDates?.incurred_start_date,
      'YYYY-MM-DD',
    ),
    incurred_end_date: formatDate(planDates?.incurred_end_date, 'YYYY-MM-DD'),
    paid_start_date: formatDate(planDates?.paid_start_date, 'YYYY-MM-DD'),
    paid_end_date: formatDate(planDates?.paid_end_date, 'YYYY-MM-DD'),
  };
};
export const getMinMaxDates = (
  current_dates: any,
  max_claim_date: string,
  min_claim_date: string,
  max_incurred_date: string,
  min_incurred_date: string,
) => {
  const minClaimDate = moment
    .min(moment(current_dates?.paid_start_date), moment(min_claim_date))
    .format('YYYY-MM-DD');
  const maxClaimDate = moment
    .max(moment(current_dates?.paid_end_date), moment(max_claim_date))
    .format('YYYY-MM-DD');

  const minIncurredDate = moment
    .min(moment(current_dates?.incurred_start_date), moment(min_incurred_date))
    .format('YYYY-MM-DD');
  const maxIncurredDate = moment
    .max(moment(current_dates?.incurred_end_date), moment(max_incurred_date))
    .format('YYYY-MM-DD');

  return { minClaimDate, maxClaimDate, minIncurredDate, maxIncurredDate };
};

type usePlanDatesReturn = {
   
  getPlanDates: () => Promise<void>;
  loading: boolean;
};

const usePlanDates = (): usePlanDatesReturn => {
  const [loading, setLoading] = useState(false);

  const employerGroupStore = useStores().employerGroupStore;
  const quaterlyReportStore = useStores().quaterlyReportStore;
  const { startDate, endDate } = getStartDateAndEndDate();

  const getPlanDates = async () => {
    const baseUrl = properties.aiqBaseUrl + properties.getPlanDates;
    const requestBody = {
      startDate,
      endDate,
      eg_nid: employerGroupStore?.employerGroupResponseData?.eg_nid,
      deductible_amount: 0,
      max_claim_date:
        employerGroupStore?.employerGroupResponseData?.max_claim_date,
    };

    setLoading(true);
    try {
      const response = await axios.post(baseUrl, requestBody);
      const dates = response.data?.data;

      if (dates?.length === 0) {
        quaterlyReportStore.setIncurredStartDate(startDate);
        quaterlyReportStore.setIncurredEndDate(endDate);
        quaterlyReportStore.setPaidStartDate('');
        quaterlyReportStore.setPaidEndDate('');
        quaterlyReportStore.setIsIncurredDate(true);
        quaterlyReportStore.setIsPaidDate(false);
        quaterlyReportStore.setAggregationParameter('incurred');
        return;
      }

      const currentDates =
        dates?.find((item: any) => item.current === true) ||
        dates[dates.length - 1];
      const currentIndex = dates?.findIndex(
        (item: any) => item.current === true,
      );
      const {
        minClaimDate,
        maxClaimDate,
        minIncurredDate,
        maxIncurredDate,
      } = getMinMaxDates(
        currentDates,
        employerGroupStore.employerGroupResponseData.max_claim_date,
        employerGroupStore.employerGroupResponseData.min_claim_date,
        employerGroupStore.employerGroupResponseData.max_incurred_date,
        employerGroupStore.employerGroupResponseData.min_incurred_date,
      );

      quaterlyReportStore.setIncurredStartDate(
        formatPlanPeriodDates(currentDates).incurred_start_date,
      );
      quaterlyReportStore.setIncurredEndDate(
        formatPlanPeriodDates(currentDates).incurred_end_date,
      );
      quaterlyReportStore.setPaidStartDate(
        formatPlanPeriodDates(currentDates).paid_start_date,
      );
      quaterlyReportStore.setPaidEndDate(
        formatPlanPeriodDates(currentDates).paid_end_date,
      );
      quaterlyReportStore.setIsIncurredDate(
        currentDates?.isIncurredDateEnabled,
      );
      quaterlyReportStore.setIsPaidDate(currentDates?.isPaidDateEnabled);
      quaterlyReportStore.setPlanDates(dates);
      quaterlyReportStore.setSelectedPlanIndex(currentIndex);
      quaterlyReportStore.setMinMaxDates(
        minIncurredDate,
        maxIncurredDate,
        minClaimDate,
        maxClaimDate,
      );
    } catch (error) {
       
      console.error('Error fetching plan dates:', error);
    } finally {
      quaterlyReportStore.setIsPlanDatesFetched(true);
      setLoading(false);
    }
  };

  return { getPlanDates, loading };
};

export default usePlanDates;
